import type { Notice } from '~/pages/authentication/components/AuthNotice'
import { useMutation } from '@apollo/client/react'
import { Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { BrandLogo } from '~/components/BrandLogo'
import { MAIN_ID } from '~/components/SkipLink'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { GQL_VERIFY_ACCOUNT } from '~/gql/auth/auth'
import AuthNotice from '~/pages/authentication/components/AuthNotice'

function ValidAccount() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [notice, setNotice] = useState<Notice | null>(null)
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    if (!token || !email) {
      navigate('/')
    }
  }, [token, email, navigate])

  const [verify, { loading, called: isCalled }] = useMutation(GQL_VERIFY_ACCOUNT)

  const onVerify = async () => {
    try {
      const { data } = await verify({
        variables: {
          email: email!,
          token: token!,
        },
      })
      if (data && data.verifyEmail) {
        setNotice({ kind: 'success', text: t('valid.account.success') })
      } else {
        setNotice({ kind: 'error', text: t('valid.account.failed') })
      }
    } catch {
      setNotice({ kind: 'error', text: t('bad.server-login.message') })
    }
  }

  return (
    <main id={MAIN_ID} className="flex w-full min-h-dvh flex-col items-center justify-center p-5">
      <Card className="sm:w-md w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <div className="flex flex-col gap-1">
            <CardTitle as="h1" className="font-heading text-2xl tracking-tight">{t('account.validate')}</CardTitle>
            <CardDescription>{t('account.validate.confirmation')}</CardDescription>
          </div>
        </CardHeader>
        <CardContent>

          <AuthNotice notice={notice} />
          <Button
            type="submit"
            className="w-full"
            disabled={isCalled || loading}
            onClick={onVerify}
          >
            {loading && <Loader className="mr-2 size-4 animate-spin" />}
            {t('common.validate')}
          </Button>

          <div className="mt-4 text-center text-sm">
            <Link to="/" className="underline underline-offset-4">
              {t('login.return-message')}
            </Link>
          </div>

        </CardContent>
      </Card>
    </main>
  )
}

export default ValidAccount
